import { useEffect, useState } from 'react'

import { USER_DROPDOWN_VARIANTS } from '#/shared/ui/user-dropdown'
import type { UserDropdownVariant, UserOption } from '#/shared/ui/user-dropdown'

import { OWNER_ALL_USERS, isSpecificOwner } from './owner-filter'

export interface OwnerUserState {
  /**
   * Resolved owner for the UserDropdown trigger. `undefined` means the
   * caller has no owner filter and `me` hasn't arrived yet — consumers
   * should skip the dropdown until this resolves instead of filling in
   * a placeholder.
   */
  currentUser: UserOption | undefined
  onSelect?: (user: UserOption) => void
  onClear?: () => void
}

/**
 * Owner-filter state for the shipment list toolbar. Remembers the
 * last-picked owner so the trigger keeps showing their name/avatar even
 * after the `q` search shrinks `users` or the page reloads with only the
 * id in the URL. Also wires the select/clear handlers consumed by the
 * UserDropdown — the teams variant keeps `onClear` live so "All Users"
 * stays clickable, while the default variant only surfaces clear once an
 * owner is actually picked.
 */
export function useOwnerUser(
  users: UserOption[],
  me: UserOption | undefined,
  ownerUserId: string | null | undefined,
  onOwnerUserChange: ((userId: string | null) => void) | undefined,
  variant: UserDropdownVariant | undefined,
  options: { defaultToMe?: boolean } = {},
): OwnerUserState {
  const { defaultToMe = true } = options
  const [ownerUser, setOwnerUser] = useState<UserOption | undefined>(undefined)
  useEffect(() => {
    if (!isSpecificOwner(ownerUserId)) {
      setOwnerUser(undefined)
      return
    }
    if (ownerUser?.id === ownerUserId) return
    const match =
      me?.id === ownerUserId ? me : users.find((u) => u.id === ownerUserId)
    if (match) setOwnerUser(match)
  }, [ownerUserId, users, me, ownerUser?.id])

  // Default the owner filter to the current user whenever the URL carries no
  // owner. This must self-heal, not run once: the detail overlay keeps the list
  // page mounted while navigating to `/orders/$orderId` with no search, which
  // strips `userId`. A one-shot guard would never re-inject it on the way back,
  // leaving `useDefaultOwnerSettled` gated and the table stuck on its skeleton.
  // `null` only ever means "not yet defaulted" — an explicit "All Users" choice
  // is the `OWNER_ALL_USERS` sentinel, which is truthy and left untouched here.
  useEffect(() => {
    if (!defaultToMe) return
    if (!me?.id || !onOwnerUserChange) return
    if (!ownerUserId) onOwnerUserChange(me.id)
  }, [defaultToMe, me, ownerUserId, onOwnerUserChange])

  const currentUser = ownerUser ?? me

  if (!onOwnerUserChange) {
    return { currentUser }
  }

  const select = (user: UserOption) => {
    setOwnerUser(user)
    onOwnerUserChange(user.id)
  }
  const clear = () => {
    setOwnerUser(undefined)
    onOwnerUserChange(OWNER_ALL_USERS)
  }
  const canClear =
    variant === USER_DROPDOWN_VARIANTS.TEAMS || Boolean(ownerUserId)
  return {
    currentUser,
    onSelect: select,
    onClear: canClear ? clear : undefined,
  }
}
